import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { scooterApi } from "@/lib/api";
import { toast } from "sonner";
import { Eye, TrendingUp, BarChart3 } from "lucide-react";

export function ViewStatsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await scooterApi.getViewStats();
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch view stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="view-stats-widget">
        <CardContent className="p-6">
          <div className="animate-pulse text-muted-foreground">Chargement des statistiques...</div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Card data-testid="view-stats-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-primary" />
          <CardTitle className="font-heading text-lg">Statistiques de vues</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total Views */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Total des vues</span>
          </div>
          <span className="font-mono font-bold text-xl text-primary">{stats.total_views || 0}</span>
        </div>

        {/* Top Scooters */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Scooters les plus consultés
          </h4>
          <div className="space-y-2">
            {stats.top_scooters?.slice(0, 5).map((scooter, index) => (
              <div
                key={scooter.scooter_id}
                className="flex items-center justify-between p-2 rounded-md bg-secondary/50 border border-border"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium truncate max-w-[150px]">{scooter.name}</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Eye className="w-3 h-3" />
                  <span className="text-sm font-mono">{scooter.view_count}</span>
                </div>
              </div>
            ))}
            {(!stats.top_scooters || stats.top_scooters.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-2">
                Aucune donnée de vue disponible
              </p>
            )}
          </div>
        </div>

        {/* Top Models */}
        {stats.top_models && stats.top_models.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              Modèles les plus recherchés
            </h4>
            <div className="space-y-1">
              {stats.top_models.slice(0, 3).map((model, index) => (
                <div
                  key={`${model.brand}-${model.model}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="truncate">
                    {model.brand} {model.model}
                  </span>
                  <span className="font-mono text-muted-foreground">
                    {model.total_views} vues ({model.scooter_count} scooters)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
